import { Button, Heading, Stack, Text, Tooltip } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import AlertModal from 'components/modals/AlertModal';
import FormEmbeddedAlertModal from 'components/modals/FormEmbeddedAlertModal';
import { textareaInput } from 'helpers/makeFormFields';
import useLockWorkshopMutation from 'hooks/mutations/useLockWorkshopMutation';
import useUnlockWorkshopMutation from 'hooks/mutations/useUnlockWorkshopMutation';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import { useTranslation } from 'react-i18next';
import { FaLock, FaLockOpen } from 'react-icons/fa';
import * as yup from 'yup';

import { DangerZoneRow } from './DangerZoneRow';

type Props = {
  workshopId: number;
};

export const LockWorkshop = ({ workshopId }: Props) => {
  const { t } = useTranslation();

  const workshopQuery = useWorkshopQuery(workshopId);
  const showUnLockButton = workshopQuery.data?.status === 800 || workshopQuery.data?.status === 850;
  const showLockButton = !showUnLockButton;

  const lockWorkshopMutation = useLockWorkshopMutation(workshopId);
  const unlockWorkshopMutation = useUnlockWorkshopMutation(workshopId);
  const isDeletionPending = workshopQuery.data?.status === 600;

  return (
    <DangerZoneRow>
      <Stack gap={0}>
        <Heading size="xxs" as="h3">
          {t('workshop:lock_workshop.title')}
        </Heading>
        <Text> {t('workshop:lock_workshop.description')}</Text>
      </Stack>

      <Tooltip label={isDeletionPending ? t('workshop:status.pending_deletion.label') : undefined} hasArrow>
        {showUnLockButton && (
          <Button
            leftIcon={<FaLockOpen />}
            minWidth="24ch"
            colorScheme="red"
            variant="solid"
            onClick={() => {
              NiceModal.show(AlertModal as any, {
                onSubmit: () => unlockWorkshopMutation.mutate({ id: workshopId }),
                content: {
                  header: t('workshop:lock_workshop.modal.unlock_title'),
                  footer: {
                    buttons: {
                      cancelCaption: t('common:cancel'),
                      actionCaption: t('workshop:lock_workshop.unlock'),
                    },
                  },
                },
              });
            }}
            disabled={isDeletionPending}
          >
            {t('workshop:lock_workshop.button.unlock')}
          </Button>
        )}
        {showLockButton && (
          <Button
            leftIcon={<FaLock />}
            minWidth="24ch"
            colorScheme="red"
            onClick={() => {
              NiceModal.show(FormEmbeddedAlertModal as any, {
                content: {
                  header: t('workshop:lock_workshop.modal.lock_title'),
                  formTestId: 'lock-workshop-modal',
                  formFields: (props: { register: any; control: any; errors: any }) => {
                    const { register, errors } = props;
                    return (
                      <>
                        {textareaInput({
                          name: 'reason',
                          label: t('workshop:lock_workshop.reason'),
                          // @ts-ignore
                          register,
                          errors,
                        })}
                      </>
                    );
                  },
                  footer: {
                    buttons: {
                      cancelCaption: t('common:cancel'),
                      actionCaption: t('workshop:lock_workshop.lock'),
                    },
                  },
                },
                onMutate: (data: { reason: string }) =>
                  lockWorkshopMutation.mutate({
                    id: workshopId,
                    lock_reason: data.reason,
                  }),
                schema: yup
                  .object({
                    reason: yup.string().required(),
                  })
                  .required(),
              });
            }}
            disabled={isDeletionPending}
          >
            {t('workshop:lock_workshop.button.lock')}
          </Button>
        )}
      </Tooltip>
    </DangerZoneRow>
  );
};
